import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

import { API_BASE_URL } from '../utils/apiBase';
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const BACKEND_URL = API_BASE_URL;

// SessionStorage key for tracking which session_ids have already been
// exchanged in this tab. Prevents the "Authentication failed" toast/loop
// when the browser swipe-back or bfcache restores the post-OAuth URL.
const CONSUMED_KEY = 'emergent_consumed_session_ids';
const MAX_REMEMBERED = 10;

const getConsumedIds = () => {
  try {
    return JSON.parse(sessionStorage.getItem(CONSUMED_KEY) || '[]');
  } catch {
    return [];
  }
};

const markConsumed = (sid) => {
  const list = getConsumedIds();
  if (!list.includes(sid)) {
    list.push(sid);
    sessionStorage.setItem(CONSUMED_KEY, JSON.stringify(list.slice(-MAX_REMEMBERED)));
  }
};

// Strip the OAuth fragment from the URL so a refresh or bfcache restore
// doesn't keep re-triggering AuthCallback on a dead session_id.
const clearHash = () => {
  if (window.location.hash) {
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
  }
};

export const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { refetchUser } = useAuth();
  const hasProcessed = useRef(false);

  // Handle bfcache restoration. If Chrome restores a snapshot of this
  // component (e.g. via swipe-back), JavaScript doesn't re-run — the user
  // would see the spinner forever. Force a clean reload to /login.
  useEffect(() => {
    const handlePageShow = (e) => {
      if (e.persisted) {
        clearHash();
        window.location.replace('/login');
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      const hash = location.hash;
      const params = new URLSearchParams(hash.substring(1));

      // OAuth cancellation or error
      if (params.get('error')) {
        clearHash();
        navigate('/login', { replace: true });
        return;
      }

      const sessionId = params.get('session_id');
      if (!sessionId) {
        clearHash();
        navigate('/login', { replace: true });
        return;
      }

      // Already exchanged in this tab? bfcache or browser back may have
      // brought the user back to the post-OAuth URL. Don't show an error —
      // just route them where they belong.
      if (getConsumedIds().includes(sessionId)) {
        clearHash();
        const authed = localStorage.getItem('auth_hint');
        navigate(authed ? '/library' : '/login', { replace: true });
        return;
      }

      try {
        const response = await axios.get(`${BACKEND_URL}/api/auth/session?session_id=${sessionId}`);
        const { user } = response.data;

        markConsumed(sessionId);
        // The session token is now set as an httpOnly cookie by the backend
        // and is never exposed to JS. Store only a non-sensitive flag so the
        // app knows to fetch /me on subsequent loads.
        localStorage.setItem('auth_hint', '1');
        localStorage.removeItem('session_token'); // legacy cleanup
        // Populate AuthContext so user persists across route changes.
        await refetchUser();
        clearHash();
        navigate('/library', { replace: true, state: { user } });
      } catch (error) {
        // The session_id is dead (one-time use, expired, or revoked).
        // Mark it consumed, scrub URL & localStorage so the user can cleanly
        // re-auth without getting stranded on an error loop.
        markConsumed(sessionId);
        localStorage.removeItem('auth_hint');
        localStorage.removeItem('session_token');
        clearHash();
        console.error('Auth callback error:', error?.response?.status, error?.response?.data || error.message);
        toast.error('Sign-in expired. Please try again.');
        navigate('/login', { replace: true });
      }
    };

    processSession();
  }, [location, navigate, refetchUser]);

  return (
    <div className="min-h-screen flex items-center justify-center" data-testid="auth-callback-loading">
      <div className="text-center">
        <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
        <p className="text-lg text-muted-foreground">Completing authentication...</p>
      </div>
    </div>
  );
};
